import type { JsonLdNode } from './types'

const JSON_LD_SCRIPT = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi

/**
 * The schema.org blocks authored inside CMS HTML (AI-generated pages ship their own
 * Organization/WebPage/FAQPage graph). The HTML sanitizer drops every `<script>`, so they are
 * pulled out here and re-rendered through `JsonLd`; blocks that aren't valid JSON are skipped.
 */
export const extractJsonLd = (html: string | null | undefined): JsonLdNode[] =>
  [...(html ?? '').matchAll(JSON_LD_SCRIPT)].flatMap(([, json]) => {
    try {
      const node: unknown = JSON.parse(json)

      return node && typeof node === 'object' && !Array.isArray(node) ? [node as JsonLdNode] : []
    } catch {
      return []
    }
  })

/** JSON-LD authored inside the widgets' own HTML (e.g. the AI-generated /commerce pages). */
export const extractWidgetsJsonLd = (widgets: { widget: object }[] | null | undefined) =>
  (widgets ?? []).flatMap(({ widget }) => {
    const { content } = widget as { content?: unknown }
    return typeof content === 'string' ? extractJsonLd(content) : []
  })

/** Whether any node, including nested `@graph` entries, has the schema.org `type`. */
export const hasJsonLdType = (nodes: JsonLdNode[], type: string): boolean =>
  nodes.some((node) => {
    const nodeType = node['@type']
    const graph = node['@graph']

    return (
      (Array.isArray(nodeType) ? nodeType.includes(type) : nodeType === type) ||
      (Array.isArray(graph) && hasJsonLdType(graph as JsonLdNode[], type))
    )
  })
